import { Recapt as RecaptEngine } from './engine';
import { RecaptFacade, bindEngine } from './facade';
import { recapt } from './methodInterface';
import { StartOptions } from './types';

bindEngine(RecaptEngine);

const facade = new RecaptFacade();

declare global {
  interface Window {
    Glimt: RecaptFacade;
    Recapt: RecaptFacade;
    glimt: typeof recapt;
    recapt: typeof recapt;
    Intercom?: any;
    zE?: (command: string, ...args: any[]) => void;
  }
}

const RECAPT_SCRIPT_SRC_MARKERS = ['glimt.js', 'recapt.js'] as const;

function ensureRecaptGlobalsOnWindow(): void {
  if (!window.Recapt) window.Recapt = facade;
  if (!window.recapt) window.recapt = recapt;
  if (!window.Glimt) window.Glimt = facade;
  if (!window.glimt) window.glimt = recapt;
}

function detectZendeskLoading(): boolean {
  try {
    const w = window as Window & {
      zE?: unknown;
      zEmbed?: unknown;
      zEACLoaded?: unknown;
      zESettings?: unknown;
      zendeskHost?: unknown;
    };

    if (w.zE || w.zEmbed || w.zEACLoaded || w.zESettings || w.zendeskHost) {
      return true;
    }

    const scripts = Array.from(document.getElementsByTagName('script'));

    if (
      scripts.some(
        (s) =>
          s.src.includes('zdassets.com') ||
          s.src.includes('zendesk.com') ||
          s.src.includes('zopim.com'),
      )
    ) {
      return true;
    }

    return scripts.some((script) => {
      const content = script.innerHTML || '';
      return (
        content.includes('zE(') ||
        content.includes('Zendesk') ||
        script.getAttribute('data-zendesk-key') !== null
      );
    });
  } catch (error) {
    console.warn('Error detecting Zendesk:', error);
    return false;
  }
}

function detectIntercomLoading(): boolean {
  try {
    if (window.Intercom) {
      return true;
    }

    const scripts = Array.from(document.getElementsByTagName('script'));

    if (
      scripts.some(
        (s) =>
          s.src.includes('intercom.io') ||
          s.src.includes('intercom.com') ||
          s.src.includes('widget.intercom.io'),
      )
    ) {
      return true;
    }

    if (
      scripts.some((script) => {
        const html = script.innerHTML || '';
        return (
          html.includes('Intercom(') ||
          html.includes('window.Intercom') ||
          script.hasAttribute('data-intercom-app-id') ||
          script.getAttribute('data-widget') === 'intercom'
        );
      })
    ) {
      return true;
    }

    const w = window as Window & {
      intercomSettings?: unknown;
      INTERCOM_APP_ID?: unknown;
    };

    return Boolean(w.intercomSettings || w.INTERCOM_APP_ID);
  } catch (error) {
    console.warn('Error detecting Intercom:', error);
    return false;
  }
}

function findRecaptScriptTag(): HTMLScriptElement | undefined {
  return Array.from(document.getElementsByTagName('script')).find((script) =>
    RECAPT_SCRIPT_SRC_MARKERS.some((marker) => script.src.includes(marker)),
  );
}

function configureIntercomIdentify(): void {
  RecaptEngine.info(
    'Intercom detected on page - adjusting Recapt initialization',
  );

  const externalId = window.Intercom('getVisitorId');
  if (externalId) {
    window.recapt('identify', {
      externalId,
      sourceProvider: 'intercom',
    });
    return;
  }

  window.Intercom('onShow', () => {
    const id = window.Intercom('getVisitorId');
    if (id) {
      window.recapt('identify', {
        externalId: id,
        sourceProvider: 'intercom',
      });
    }
  });
}

function setupZendeskIntegration(): void {
  function waitForZendesk(): void {
    if (typeof window.zE !== 'function') {
      setTimeout(waitForZendesk, 100);
      return;
    }

    console.log('Zendesk detected - setting up session tracking');
    RecaptEngine.once('session:id:set', (sessionId) => {
      console.log('Session ID set - setting conversation tags', sessionId);
      window.zE!('messenger:set', 'conversationTags', [
        `recapt_session_id:${sessionId}`,
      ]);

      const identity = RecaptEngine.session.getIdentity();
      if (identity.uid || identity.externalId || identity.email) {
        window.glimt('identify', {
          sourceProvider: 'zendesk',
        });
      }
    });
  }

  setTimeout(waitForZendesk, 100);
}

function applyScriptTagOptions(
  script: HTMLScriptElement,
  config: StartOptions,
): void {
  if (script.getAttribute('data-system-painpoints') !== 'false') {
    RecaptEngine.useSystemPainpoints();
  }

  if (script.getAttribute('data-persist') !== 'false') {
    RecaptEngine.persist();
  }

  if (script.getAttribute('data-enable-user-comments') === 'true') {
    RecaptEngine.enableUserComments();
  }

  const disableRefetchesCausedByCors = script.getAttribute(
    'data-disable-refetches-caused-by-cors',
  );
  config.recordOptions = {
    disableRefetchesCausedByCors: disableRefetchesCausedByCors !== 'false',
  };
}

function initializeFromScript(): void {
  try {
    const currentScript = findRecaptScriptTag();
    if (!currentScript) {
      return;
    }

    const apiKey = currentScript.getAttribute('data-public-key');
    if (!apiKey) {
      return;
    }

    const isDebug = currentScript.getAttribute('data-debug') === 'true';
    if (isDebug) console.log('[DEBUG] Initializing Recapt from script tag');

    const intercomDetected = detectIntercomLoading();
    const zendeskDetected = detectZendeskLoading();

    const config: StartOptions = {
      apiKey,
      intercomDetected,
      zendeskDetected,
      debug: isDebug,
    };

    ensureRecaptGlobalsOnWindow();

    if (intercomDetected) {
      configureIntercomIdentify();
    }

    if (zendeskDetected) {
      setupZendeskIntegration();
    }

    applyScriptTagOptions(currentScript, config);

    if (!RecaptEngine.running) {
      RecaptEngine.start(config);
    }
  } catch (error) {
    console.warn('Error initializing Recapt:', error);
  }
}

if (typeof window !== 'undefined') {
  ensureRecaptGlobalsOnWindow();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeFromScript);
  } else {
    // Run after React hydration
    requestAnimationFrame(() => {
      initializeFromScript();
    });
  }
}
